import type { IncomeOverview } from '@sea-snag/shared';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { useIncomeOverview } from '../../hooks/use-income';
import { incomeApi } from '../../lib/api-client';
import {
  filterBoats,
  formatCents,
  type IncomeFilters,
  type IncomeMetric,
  monthlySeries,
  NO_FILTERS,
  totalsOf,
} from '../../lib/income';
import {
  boatComparisonRows,
  change,
  type Commercial,
  commercial,
  observation,
  sameBoatsOnly,
} from '../../lib/income-compare';
import { queryKeys } from '../../lib/query-keys';
import { IncomeFiltersBar } from './income-filters';
import { MetricPicker, METRICS } from './income-monthly-chart';

type Mode = 'samePoint' | 'latest';
type Scope = 'whole' | 'same';

const PER_WEEK_INFO =
  'Net charter revenue from confirmed guest bookings divided by their booked charter weeks. Owner bookings are excluded from both figures to show commercial performance.';

const pct = (p: number | null) => (p === null ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1)}%`);

/**
 * Income → Year comparison (#217): one season against another, read-only,
 * from the same figures as the Overview. "Same point last year" needs each
 * season as recorded on a date, which isn't kept yet, so it says so rather
 * than showing the latest figures in its place.
 */
export function IncomeComparison({ orgPid, year }: { orgPid: string; year: number }) {
  const [mainYear, setMainYear] = useState(year);
  const [compareYear, setCompareYear] = useState(year - 1);
  const [mode, setMode] = useState<Mode>('samePoint');
  const [scope, setScope] = useState<Scope>('whole');
  const [filters, setFilters] = useState<IncomeFilters>(NO_FILTERS);
  const [metric, setMetric] = useState<IncomeMetric>('net');
  const [view, setView] = useState<'income' | 'weeks'>('income');

  const years = useQuery({
    queryKey: queryKeys.analytics.incomeYears(orgPid),
    queryFn: () => incomeApi.years(),
    enabled: !!orgPid,
  });
  const main = useIncomeOverview(orgPid, mainYear);
  const compare = useIncomeOverview(orgPid, compareYear);
  const seasons = useMemo(
    () =>
      [...new Set([...(years.data ?? []), year, year - 1, mainYear, compareYear])].sort(
        (a, b) => b - a
      ),
    [years.data, year, mainYear, compareYear]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <SeasonSelect
          label="Main season"
          value={mainYear}
          seasons={seasons}
          onChange={setMainYear}
        />
        <SeasonSelect
          label="Compare with"
          value={compareYear}
          seasons={seasons.filter((s) => s !== mainYear)}
          onChange={setCompareYear}
        />
        <Select value="latest" disabled>
          <SelectTrigger className="h-8 w-36 text-sm" aria-label="As of date">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="latest">As of: latest</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Toggle
          label="Comparison mode"
          value={mode}
          options={[
            { value: 'samePoint', label: 'Same point last year' },
            { value: 'latest', label: 'Latest recorded season results' },
          ]}
          onChange={setMode}
        />
        <Toggle
          label="Fleet"
          value={scope}
          options={[
            { value: 'whole', label: 'Whole fleet' },
            { value: 'same', label: 'Same boats only' },
          ]}
          onChange={setScope}
        />
      </div>

      {mode === 'samePoint' ? (
        <Alert>
          <Info className="size-4" />
          <AlertDescription className="space-y-2 text-sm">
            <p>
              Historical data unavailable. A same-point comparison needs the {compareYear} season as
              it was recorded on the same date a year ago, and the app has only just started keeping
              each season&apos;s bookings as they change. Until a year of that history exists, a
              same-point figure can&apos;t be shown, and the latest figures are never put in its
              place.
            </p>
            <Button variant="outline" size="sm" onClick={() => setMode('latest')}>
              Compare the latest recorded season results instead
            </Button>
          </AlertDescription>
        </Alert>
      ) : main.isLoading || compare.isLoading || !main.data || !compare.data ? (
        <Skeleton className="h-[480px] w-full" />
      ) : (
        <Comparison
          main={main.data}
          compare={compare.data}
          scope={scope}
          filters={filters}
          onFilters={setFilters}
          metric={metric}
          onMetric={setMetric}
          view={view}
          onView={setView}
        />
      )}
    </div>
  );
}

function Comparison({
  main,
  compare,
  scope,
  filters,
  onFilters,
  metric,
  onMetric,
  view,
  onView,
}: {
  main: IncomeOverview;
  compare: IncomeOverview;
  scope: Scope;
  filters: IncomeFilters;
  onFilters: (f: IncomeFilters) => void;
  metric: IncomeMetric;
  onMetric: (m: IncomeMetric) => void;
  view: 'income' | 'weeks';
  onView: (v: 'income' | 'weeks') => void;
}) {
  const currency = main.currencies[0] ?? compare.currencies[0] ?? 'EUR';
  // Each season filtered by its own settings: an ownership change can move a boat.
  const filteredMain = filterBoats(main.boats, filters);
  const filteredCompare = filterBoats(compare.boats, filters);
  const [mainBoats, compareBoats] =
    scope === 'same'
      ? sameBoatsOnly(filteredMain, filteredCompare)
      : [filteredMain, filteredCompare];
  const mainTotals = totalsOf(mainBoats, currency);
  const compareTotals = totalsOf(compareBoats, currency);
  const mainCommercial = commercial(mainTotals);
  const compareCommercial = commercial(compareTotals);
  const pids = (boats: typeof mainBoats) => new Set(boats.map((b) => b.boatPid));
  const fleetChanged =
    scope === 'whole' &&
    (mainBoats.length !== compareBoats.length ||
      [...pids(mainBoats)].some((p) => !pids(compareBoats).has(p)));
  const reading = observation(mainCommercial, compareCommercial, {
    sameBoats: scope === 'same',
    fleetChanged,
  });

  return (
    <div className="space-y-6">
      <IncomeFiltersBar
        boats={[...new Map([...main.boats, ...compare.boats].map((b) => [b.boatPid, b])).values()]}
        filters={filters}
        onChange={onFilters}
        included={new Set([...mainBoats, ...compareBoats].map((b) => b.boatPid)).size}
      />
      <p className="text-sm text-muted-foreground">
        Comparing the latest recorded results of the {main.year} season with those of the{' '}
        {compare.year} season. {scope === 'whole' ? 'Whole fleet' : 'Same boats only'}:{' '}
        {mainBoats.length} {mainBoats.length === 1 ? 'boat' : 'boats'} versus {compareBoats.length}.
        A past season&apos;s latest record can still change if MMK amends a booking.
      </p>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {METRICS.map((m) => {
          const pick = (t: typeof mainTotals) =>
            m.value === 'gross'
              ? t.grossCents
              : m.value === 'net'
                ? t.netCents
                : m.value === 'owner'
                  ? t.ownerCents
                  : t.companyCents;
          const incomplete =
            (m.value === 'owner' || m.value === 'company') &&
            (mainTotals.unresolvedBoats > 0 || compareTotals.unresolvedBoats > 0);
          return (
            <CompareCard
              key={m.value}
              title={m.label}
              main={pick(mainTotals)}
              compare={pick(compareTotals)}
              mainYear={main.year}
              compareYear={compare.year}
              currency={currency}
              incomplete={incomplete}
            />
          );
        })}
      </div>

      <CommercialSection
        main={mainCommercial}
        compare={compareCommercial}
        mainYear={main.year}
        compareYear={compare.year}
        currency={currency}
        reading={reading}
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">By month charters ended</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <MetricPicker metric={metric} onChange={onMetric} />
          <MonthlyComparisonChart
            main={main}
            compare={compare}
            mainBoats={mainBoats}
            compareBoats={compareBoats}
            currency={currency}
            metric={metric}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
          <CardTitle className="text-base">Per boat</CardTitle>
          <Toggle
            label="Table"
            value={view}
            options={[
              { value: 'income', label: 'Income breakdown' },
              { value: 'weeks', label: 'Weeks and revenue' },
            ]}
            onChange={onView}
          />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {view === 'income' ? (
            <IncomeBreakdownTable
              mainBoats={mainBoats}
              compareBoats={compareBoats}
              currency={currency}
              metric={metric}
              mainYear={main.year}
              compareYear={compare.year}
            />
          ) : (
            <WeeksTable
              mainBoats={mainBoats}
              compareBoats={compareBoats}
              currency={currency}
              mainYear={main.year}
              compareYear={compare.year}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CompareCard({
  title,
  main,
  compare,
  mainYear,
  compareYear,
  currency,
  incomplete,
}: {
  title: string;
  main: number;
  compare: number;
  mainYear: number;
  compareYear: number;
  currency: string;
  incomplete: boolean;
}) {
  const ch = change(main, compare);
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="text-2xl font-bold tabular-nums">{formatCents(main, currency)}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {compareYear}: {formatCents(compare, currency)}
        </div>
        {incomplete ? (
          <div className="text-xs text-muted-foreground">
            Change not shown: some boats have no owner share or ownership type in {mainYear} or{' '}
            {compareYear}.
          </div>
        ) : (
          <div className="text-xs tabular-nums">
            {ch.diff >= 0 ? '+' : ''}
            {formatCents(ch.diff, currency)} · {pct(ch.pct)}
            {ch.pct === null && compare <= 0 && (
              <span className="text-muted-foreground"> (nothing in {compareYear} to compare)</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CommercialSection({
  main,
  compare,
  mainYear,
  compareYear,
  currency,
  reading,
}: {
  main: Commercial;
  compare: Commercial;
  mainYear: number;
  compareYear: number;
  currency: string;
  reading: string | null;
}) {
  const rows: Array<{ label: string; main: string; compare: string; change: string }> = [
    {
      label: 'Guest bookings',
      main: String(main.guestBookings),
      compare: String(compare.guestBookings),
      change: pct(change(main.guestBookings, compare.guestBookings).pct),
    },
    {
      label: 'Guest charter weeks',
      main: main.guestWeeks.toFixed(1),
      compare: compare.guestWeeks.toFixed(1),
      change: pct(change(main.guestWeeks, compare.guestWeeks).pct),
    },
    {
      label: 'Guest net revenue',
      main: formatCents(main.guestNetCents, currency),
      compare: formatCents(compare.guestNetCents, currency),
      change: pct(change(main.guestNetCents, compare.guestNetCents).pct),
    },
    {
      label: 'Net revenue per sold week',
      main: main.perWeekCents === null ? '—' : formatCents(Math.round(main.perWeekCents), currency),
      compare:
        compare.perWeekCents === null
          ? '—'
          : formatCents(Math.round(compare.perWeekCents), currency),
      change:
        main.perWeekCents === null || compare.perWeekCents === null
          ? '—'
          : pct(change(main.perWeekCents, compare.perWeekCents).pct),
    },
  ];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1 text-base">
          Booking volume and revenue per sold week
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label={PER_WEEK_INFO} className="text-muted-foreground">
                  <Info className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">{PER_WEEK_INFO}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead />
              <TableHead className="text-right">{mainYear}</TableHead>
              <TableHead className="text-right">{compareYear}</TableHead>
              <TableHead className="text-right">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.label}>
                <TableCell>{r.label}</TableCell>
                <TableCell className="text-right tabular-nums">{r.main}</TableCell>
                <TableCell className="text-right tabular-nums">{r.compare}</TableCell>
                <TableCell className="text-right tabular-nums">{r.change}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {reading && <p className="text-sm">{reading}</p>}
        <p className="text-xs text-muted-foreground">
          Revenue per sold week isn&apos;t profit: tax, boat mix, season mix, base prices and
          discounts all move it.
        </p>
      </CardContent>
    </Card>
  );
}

const compareChartConfig = {
  main: { label: 'Main season', color: 'var(--chart-1)' },
  compare: { label: 'Comparison season', color: 'var(--chart-2)' },
} satisfies ChartConfig;

/** Two bars per month: the main season and the comparison season, by the month charters ended. */
function MonthlyComparisonChart({
  main,
  compare,
  mainBoats,
  compareBoats,
  currency,
  metric,
}: {
  main: IncomeOverview;
  compare: IncomeOverview;
  mainBoats: IncomeOverview['boats'];
  compareBoats: IncomeOverview['boats'];
  currency: string;
  metric: IncomeMetric;
}) {
  const a = monthlySeries(mainBoats, currency, main.year, metric);
  const b = monthlySeries(compareBoats, currency, compare.year, metric);
  // Line the seasons up by month of the season, then the next-year months.
  const key = (year: number, p: { year: number; month: number }) =>
    `${p.year > year ? 'next-' : ''}${p.month}`;
  const keys = [
    ...new Set([...a.map((p) => key(main.year, p)), ...b.map((p) => key(compare.year, p))]),
  ];
  const data = keys.map((k) => {
    const pa = a.find((p) => key(main.year, p) === k);
    const pb = b.find((p) => key(compare.year, p) === k);
    const label = (pa ?? pb)!.label.split(' ')[0]! + (k.startsWith('next-') ? ' (next year)' : '');
    return {
      label,
      main: pa ? pa.cents / 100 : null,
      compare: pb ? pb.cents / 100 : null,
      mainCents: pa?.cents ?? null,
      compareCents: pb?.cents ?? null,
      incomplete: Boolean(pa?.incomplete || pb?.incomplete),
    };
  });
  const compact = new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const config = {
    main: { ...compareChartConfig.main, label: String(main.year) },
    compare: { ...compareChartConfig.compare, label: String(compare.year) },
  } satisfies ChartConfig;

  return (
    <>
      <ChartContainer config={config} className="h-[260px] w-full">
        <BarChart
          data={data}
          accessibilityLayer
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} fontSize={11} />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={56}
            tickFormatter={(v: number) => compact.format(v)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(_value, name, item) => {
                  const p = item.payload as (typeof data)[number];
                  const cents = name === 'main' ? p.mainCents : p.compareCents;
                  const year = name === 'main' ? main.year : compare.year;
                  const diff =
                    name === 'main' && p.mainCents !== null && p.compareCents !== null
                      ? change(p.mainCents, p.compareCents)
                      : null;
                  return (
                    <span className="tabular-nums">
                      {year}: {cents === null ? '—' : formatCents(cents, currency, 2)}
                      {diff && (
                        <span className="ml-1 text-muted-foreground">
                          ({diff.diff >= 0 ? '+' : ''}
                          {formatCents(diff.diff, currency, 2)}, {pct(diff.pct)})
                        </span>
                      )}
                      {p.incomplete && (
                        <span className="ml-1 text-muted-foreground">(incomplete)</span>
                      )}
                    </span>
                  );
                }}
              />
            }
          />
          <Bar dataKey="main" fill="var(--color-main)" radius={[3, 3, 0, 0]} />
          <Bar dataKey="compare" fill="var(--color-compare)" radius={[3, 3, 0, 0]} />
          {/* Each season's colour named by its year, not only on hover (brief §10). */}
          <ChartLegend content={<ChartLegendContent />} />
        </BarChart>
      </ChartContainer>
      {data.some((d) => d.incomplete) && (
        <p className="text-xs text-muted-foreground">
          Some months are incomplete: boats in them have no owner share or ownership type, so only
          the part that can be split is shown.
        </p>
      )}
    </>
  );
}

const STATUS_LABEL = {
  both: null,
  newToFleet: 'New to fleet',
  notInSelectedSeason: 'Not in selected season',
} as const;

function IncomeBreakdownTable({
  mainBoats,
  compareBoats,
  currency,
  metric,
  mainYear,
  compareYear,
}: {
  mainBoats: IncomeOverview['boats'];
  compareBoats: IncomeOverview['boats'];
  currency: string;
  metric: IncomeMetric;
  mainYear: number;
  compareYear: number;
}) {
  const [sortBy, setSortBy] = useState<'name' | 'main' | 'diff'>('name');
  const rows = boatComparisonRows(mainBoats, compareBoats, currency, metric).sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    const x = sortBy === 'main' ? a.main : a.diff;
    const y = sortBy === 'main' ? b.main : b.diff;
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    return y - x;
  });
  const money = (c: number | null) => (c === null ? '—' : formatCents(c, currency));
  const label = METRICS.find((m) => m.value === metric)!.label;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <button type="button" onClick={() => setSortBy('name')}>
              Boat
            </button>
          </TableHead>
          <TableHead className="text-right">
            <button type="button" onClick={() => setSortBy('main')}>
              {label}, {mainYear}
            </button>
          </TableHead>
          <TableHead className="text-right">
            {label}, {compareYear}
          </TableHead>
          <TableHead className="text-right">
            <button type="button" onClick={() => setSortBy('diff')}>
              Change €
            </button>
          </TableHead>
          <TableHead className="text-right">Change %</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.boatPid}>
            <TableCell>
              <div className="font-medium">{r.name}</div>
              {STATUS_LABEL[r.status] && (
                <div className="text-xs text-muted-foreground">
                  {STATUS_LABEL[r.status]}: not comparable
                </div>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">{money(r.main)}</TableCell>
            <TableCell className="text-right tabular-nums">{money(r.compare)}</TableCell>
            <TableCell className="text-right tabular-nums">{money(r.diff)}</TableCell>
            <TableCell className="text-right tabular-nums">{pct(r.pct)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function WeeksTable({
  mainBoats,
  compareBoats,
  currency,
  mainYear,
  compareYear,
}: {
  mainBoats: IncomeOverview['boats'];
  compareBoats: IncomeOverview['boats'];
  currency: string;
  mainYear: number;
  compareYear: number;
}) {
  const pids = [...new Set([...mainBoats, ...compareBoats].map((b) => b.boatPid))];
  const rows = pids
    .map((pid) => {
      const m = mainBoats.find((b) => b.boatPid === pid);
      const c = compareBoats.find((b) => b.boatPid === pid);
      const cm = m ? commercial(totalsOf([m], currency)) : null;
      const cc = c ? commercial(totalsOf([c], currency)) : null;
      return { pid, name: (m ?? c)!.name, cm, cc, inBoth: Boolean(m && c), m };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  const perWeek = (x: Commercial | null) =>
    x?.perWeekCents == null ? '—' : formatCents(Math.round(x.perWeekCents), currency);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Boat</TableHead>
          <TableHead className="text-right">Guest weeks {mainYear}</TableHead>
          <TableHead className="text-right">{compareYear}</TableHead>
          <TableHead className="text-right">Guest net {mainYear}</TableHead>
          <TableHead className="text-right">{compareYear}</TableHead>
          <TableHead className="text-right">Per sold week {mainYear}</TableHead>
          <TableHead className="text-right">{compareYear}</TableHead>
          <TableHead className="text-right">Change</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.pid}>
            <TableCell>
              <div className="font-medium">{r.name}</div>
              {!r.inBoth && (
                <div className="text-xs text-muted-foreground">
                  {r.m ? 'New to fleet' : 'Not in selected season'}: not comparable
                </div>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {r.cm ? r.cm.guestWeeks.toFixed(1) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {r.cc ? r.cc.guestWeeks.toFixed(1) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {r.cm ? formatCents(r.cm.guestNetCents, currency) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {r.cc ? formatCents(r.cc.guestNetCents, currency) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">{perWeek(r.cm)}</TableCell>
            <TableCell className="text-right tabular-nums">{perWeek(r.cc)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {r.inBoth && r.cm?.perWeekCents != null && r.cc?.perWeekCents != null
                ? pct(change(r.cm.perWeekCents, r.cc.perWeekCents).pct)
                : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SeasonSelect({
  label,
  value,
  seasons,
  onChange,
}: {
  label: string;
  value: number;
  seasons: number[];
  onChange: (year: number) => void;
}) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-8 w-40 text-sm" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {seasons.map((s) => (
          <SelectItem key={s} value={String(s)}>
            {label}: {s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Toggle<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex rounded-md border" role="group" aria-label={label}>
      {options.map((o) => (
        <Button
          key={o.value}
          variant={value === o.value ? 'default' : 'ghost'}
          size="sm"
          className="h-8 rounded-none px-3 text-xs first:rounded-l-md last:rounded-r-md"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}
